import { gql } from '@apollo/client';

/**
 * ADD_NEW_CARD_CUSTOMER_TASK_MUTATION
 *
 * Creates a customer task for ordering a new card.
 *
 * @param cardTypeId - 1 = credit card (Tankkort/Företagskort), 2 = prepaid card (Smartkort)
 * @param customerId - The customer's numeric ID
 * @param pinCode    - The chosen 4-digit PIN as an integer
 * @param reference1 - First reference field (card holder / payer name, can be empty)
 * @param reference2 - Second reference field (card name / label)
 */
/**
 * ADD_REPLACEMENT_CARD_CUSTOMER_TASK_MUTATION
 *
 * Creates a customer task to replace an existing card.
 *
 * @param cardId               - The ID of the card to replace
 * @param customerId           - The customer's numeric ID
 * @param disableDueDate       - When to disable the old card (null if disableNow is true)
 * @param disableNow           - Whether to disable the old card immediately
 * @param hasApprovedCreditCheck - Whether a credit check has been approved (null = unchecked)
 * @param inheritPinCode       - Whether to inherit the PIN from the old card
 * @param pinCode              - New 4-digit PIN (null if inheritPinCode is true)
 * @param reference1           - First reference field (always empty string)
 * @param reference2           - Second reference field (card name/label)
 * @param replacementDueDate   - When to replace the old card (today if disableNow, else null)
 */
/**
 * ADD_CUSTOMER_TASK_MUTATION
 *
 * Creates a generic customer task (e.g. account deletion request).
 *
 * @param customerId  - The customer's numeric ID
 * @param description - Free-text description of the task
 * @param dueDate     - Optional due date (DateTime or null)
 */
export const ADD_CUSTOMER_TASK_MUTATION = gql`
	mutation AddCustomerTask(
		$customerId: Int!
		$description: String!
		$dueDate: DateTime
	) {
		addCustomerTask(
			input: {
				customerId: $customerId
				description: $description
				dueDate: $dueDate
			}
		) {
			customerTask {
				closedAt
				createdAt
				customerId
				description
				dueDate
				id
				type {
					id
					name
				}
			}
		}
	}
`;

export const ADD_REPLACEMENT_CARD_CUSTOMER_TASK_MUTATION = gql`
	mutation AddReplacementCardCustomerTask(
		$cardId: Int!
		$customerId: Int!
		$disableDueDate: DateTime
		$disableNow: Boolean!
		$hasApprovedCreditCheck: Boolean!
		$inheritPinCode: Boolean!
		$pinCode: Int
		$reference1: String!
		$reference2: String!
		$replacementDueDate: DateTime
	) {
		addReplacementCardCustomerTask(
			input: {
				cardId: $cardId
				customerId: $customerId
				disableDueDate: $disableDueDate
				disableNow: $disableNow
				hasApprovedCreditCheck: $hasApprovedCreditCheck
				inheritPinCode: $inheritPinCode
				pinCode: $pinCode
				reference1: $reference1
				reference2: $reference2
				replacementDueDate: $replacementDueDate
			}
		) {
			customerTask {
				closedAt
				createdAt
				customerId
				description
				dueDate
				id
			}
		}
	}
`;

export const ADD_NEW_CARD_CUSTOMER_TASK_MUTATION = gql`
	mutation AddNewCardCustomerTask(
		$cardTypeId: Byte!
		$customerId: Int!
		$pinCode: Int!
		$reference1: String!
		$reference2: String!
	) {
		addNewCardCustomerTask(
			input: {
				cardTypeId: $cardTypeId
				customerId: $customerId
				pinCode: $pinCode
				reference1: $reference1
				reference2: $reference2
			}
		) {
			customerTask {
				closedAt
				createdAt
				customerId
				description
				dueDate
				id
				type {
					id
					name
				}
			}
		}
	}
`;
